import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarBlank, CalendarX, Checks, ChatsCircle, ClipboardText, FileText, ImagesSquare, Microphone, Notebook, Sparkle, Wallet } from "@phosphor-icons/react";
import { TeacherTimetableTab } from "@/pages/Timetable";
import ReportCard from "@/components/ReportCard";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { API, getCsrfToken, formatErr, fmtDate, todayLocal } from "@/lib/api";
import { DailyReport, MonthlyReport } from "@/components/attendance/AttendanceReports";
import { MarkAttendance } from "@/components/attendance/MarkAttendance";
import TeacherMessages from "@/components/messaging/TeacherMessages";
import TeacherLearning from "@/components/TeacherLearning";
import { TeacherMoments } from "@/components/MediaMoments";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";

const NAV = [
  { id: "attendance", label: "Attendance", labelKey: "navigation.attendance", icon: Checks },
  { id: "homework", label: "Homework & Activities", labelKey: "teacherNavigation.homeworkActivities", icon: ClipboardText },
  { id: "class-moments", label: "Class Moments", labelKey: "teacherNavigation.classMoments", icon: ImagesSquare },
  { id: "messages", label: "Parent Messages", labelKey: "teacherNavigation.parentMessages", icon: ChatsCircle },
  { id: "exams", label: "Exams", labelKey: "navigation.exams", icon: Notebook },
  { id: "report-cards", label: "Report Cards", labelKey: "navigation.reportCards", icon: FileText },
  { id: "timetable", label: "My Timetable", labelKey: "teacherNavigation.myTimetable", icon: CalendarBlank },
  { id: "leave", label: "My Leave", labelKey: "teacherNavigation.myLeave", icon: CalendarX },
  { id: "fee-confirmations", label: "Fee Confirmations", icon: Wallet },
  { id: "learning", label: "Learning", icon: Notebook },
];

export default function Teacher() {
  const [tab, setTab] = useState("attendance");
  const [classes, setClasses] = useState([]);
  const [optIn, setOptIn] = useState(true);

  useEffect(() => {
    api.get("/teacher/classes").then((r) => setClasses(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/teacher/school-info").then((r) => setOptIn(r.data.notifications_opt_in)).catch(() => {});
  }, []);

  return (
    <DashboardShell brand="Teacher" nav={NAV} active={tab} onNavigate={setTab}>
      {tab === "attendance" && <AttendanceTab classes={classes} optIn={optIn} />}
      {tab === "homework" && <HomeworkTab classes={classes} optIn={optIn} />}
      {tab === "class-moments" && <TeacherMoments classes={classes} />}
      {tab === "messages" && <TeacherMessages />}
      {tab === "exams" && <TeacherExamsTab />}
      {tab === "report-cards" && <TeacherReportCardsTab />}
      {tab === "timetable" && <TeacherTimetableTab />}
      {tab === "leave" && <TeacherLeaveTab />}
      {tab === "fee-confirmations" && <TeacherFeeConfirmations />}
      {tab === "learning" && <TeacherLearning />}
    </DashboardShell>
  );
}

function AttendanceTab({ classes, optIn }) {
  const { t } = useTranslation();
  const [view, setView] = useState("mark");

  const attendanceViews = [
    ["mark", t("teacherAttendance.markAttendance", { defaultValue: "Mark attendance" })],
    ["daily", t("teacherAttendance.dailyView", { defaultValue: "Daily view" })],
    ["monthly", t("teacherAttendance.monthlyView", { defaultValue: "Monthly view" })],
  ];

  return (
    <div data-testid="attendance-tab">
      <div className="flex gap-2 mb-4" data-testid="attendance-view-tabs">
        {attendanceViews.map(([id, label]) => (
          <button
            key={id}
            data-testid={`attendance-view-${id}`}
            onClick={() => setView(id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors duration-200 ${view === id ? "bg-[#00194E] text-white" : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {view !== "mark" ? (
        view === "daily" ? (
          <DailyReport apiBase="/teacher/attendance" classes={classes} />
        ) : (
          <MonthlyReport apiBase="/teacher/attendance" classes={classes} />
        )
      ) : (
        <MarkAttendance optIn={optIn} />
      )}
    </div>
  );
}

const HOMEWORK_CONTENT_LANGUAGES = [
  { code: "en", label: "English", speech: "en-IN" },
  { code: "hi", label: "हिन्दी", speech: "hi-IN" },
  { code: "kn", label: "ಕನ್ನಡ", speech: "kn-IN" },
  { code: "mr", label: "मराठी", speech: "mr-IN" },
  { code: "te", label: "తెలుగు", speech: "te-IN" },
];

const homeworkLanguage = (code) =>
  HOMEWORK_CONTENT_LANGUAGES.find((item) => item.code === (code || "en")) ||
  HOMEWORK_CONTENT_LANGUAGES[0];

function HomeworkTab({ classes, optIn }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({
    class_id: "",
    kind: "homework",
    subject: "",
    title: "",
    content: "",
    content_language: "en",
  });
  const [channels, setChannels] = useState({ send_email: false, send_whatsapp: false, send_sms: false });
  const [posting, setPosting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  const load = () => {
    api.get("/teacher/homework").then((r) => setPosts(r.data)).catch((e) => toast.error(formatErr(e)));
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!classes.length) return;

    setForm((f) =>
      f.class_id
        ? f
        : { ...f, class_id: classes[0].id }
    );
  }, [classes]);

  const selectedClass = classes.find((c) => c.id === form.class_id);

  const post = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const { data } = await api.post("/teacher/homework", { ...form, ...channels });
      const a = data.alerts;
      toast.success(t("teacherHomework.postedSuccess", { defaultValue: "Posted to the class feed" }) + (a ? ` · ${t("teacherHomework.alerts", { defaultValue: "alerts" })}: ${a.email_sent} ${t("teacherHomework.email", { defaultValue: "email" })}, ${a.whatsapp_sent} WhatsApp, ${a.sms_sent || 0} SMS ${t("teacherHomework.sent", { defaultValue: "sent" })}` : ""));
      setForm((f) => ({ ...f, subject: "", title: "", content: "" }));
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setPosting(false);
    }
  };

  const suggestWithAI = async () => {
    if (!selectedClass) {
      toast.error(t("teacherHomework.selectClassFirst", { defaultValue: "Select a class first" }));
      return;
    }
    if (!form.subject.trim()) {
      toast.error(t("teacherHomework.addSubjectFirst", { defaultValue: "Add a subject first (e.g. English, Maths)" }));
      return;
    }
    setAiBusy(true);
    setForm((f) => ({ ...f, content: "" }));
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`${API}/teacher/ai-suggest`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
        },
        body: JSON.stringify({
          grade: selectedClass.grade || selectedClass.name,
          subject: form.subject,
          kind: form.kind,
          topic: form.title,
          content_language: form.content_language,
        }),
      });
      if (!res.ok) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          const payload = chunk.slice(6).trim();
          if (payload === "[DONE]") break;
          const parsed = JSON.parse(payload);
          if (parsed.error) throw new Error(parsed.error);
          if (parsed.delta) setForm((f) => ({ ...f, content: f.content + parsed.delta }));
        }
      }
      if (!form.title.trim()) setForm((f) => ({ ...f, title: `${f.subject} ${f.kind === "activity" ? "Activity" : "Homework"}` }));
    } catch (err) {
      toast.error(t("teacherHomework.aiSuggestionFailed", { defaultValue: "AI suggestion failed — please try again" }));
    } finally {
      setAiBusy(false);
    }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error(t("teacherHomework.voiceNotSupported", { defaultValue: "Voice typing isn't supported in this browser" }));
      return;
    }
    if (listening) {
      recogRef.current?.stop();
      setListening(false);
      return;
    }
    const recog = new SR();
    recog.lang = homeworkLanguage(form.content_language).speech;
    recog.continuous = true;
    recog.interimResults = false;
    recog.onresult = (e) => {
      const text = Array.from(e.results).slice(e.resultIndex).map((r) => r[0].transcript).join(" ");
      if (text) setForm((f) => ({ ...f, content: (f.content ? f.content + " " : "") + text.trim() }));
    };
    recog.onend = () => setListening(false);
    recog.onerror = () => setListening(false);
    recogRef.current = recog;
    recog.start();
    setListening(true);
  };

  const kindBadge = { homework: "bg-brand-coral/10 text-brand-coralDark", activity: "bg-brand-mint/15 text-teal-700", note: "bg-status-blue/10 text-status-blue" };

  return (
    <div data-testid="homework-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-6">{t("teacherHomework.heading", { defaultValue: "Homework & activities" })}</h1>
      <form onSubmit={post} className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="homework-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label>{t("teacherHomework.class", { defaultValue: "Class" })}</Label>
            <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
              <SelectTrigger data-testid="homework-class-select" className="mt-1.5"><SelectValue placeholder={t("teacherHomework.selectClass", { defaultValue: "Select class" })} /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("teacherHomework.type", { defaultValue: "Type" })}</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="homework-kind-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="homework">{t("teacherHomework.homework", { defaultValue: "Homework" })}</SelectItem>
                <SelectItem value="activity">{t("teacherHomework.activity", { defaultValue: "Activity" })}</SelectItem>
                <SelectItem value="note">{t("teacherHomework.noteForParents", { defaultValue: "Note for parents" })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t("teacherHomework.subject", { defaultValue: "Subject" })}</Label><Input data-testid="homework-subject-input" required className="mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="English" /></div>
          <div>
            <Label>Content language</Label>
            <Select
              value={form.content_language}
              onValueChange={(value) =>
                setForm({ ...form, content_language: value })
              }
            >
              <SelectTrigger
                data-testid="homework-language-select"
                className="mt-1.5"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOMEWORK_CONTENT_LANGUAGES.map((language) => (
                  <SelectItem key={language.code} value={language.code}>
                    {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4"><Label>{t("teacherHomework.title", { defaultValue: "Title" })}</Label><Input data-testid="homework-title-input" required className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Rhyme practice" /></div>
        <div className="mt-4">
          <Label>{t("teacherHomework.details", { defaultValue: "Details" })}</Label>
          <div className="relative mt-1.5">
            <Textarea
              data-testid="homework-content-input"
              lang={form.content_language}
              required
              rows={4}
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={t("teacherHomework.detailsPlaceholder", { defaultValue: "Write or dictate today's homework…" })} className="pr-24" />
            <div className="absolute right-3 bottom-3 flex gap-2">
              <button
                type="button"
                data-testid="mic-dictation-btn"
                onClick={toggleMic}
                title={t("teacherHomework.dictateVoice", { defaultValue: "Dictate with your voice" })}
                className={`relative w-10 h-10 rounded-full grid place-items-center transition-all duration-200 active:scale-95 ${
                  listening ? "bg-status-red text-white" : "bg-slate-100 text-slate-600 hover:bg-brand-coral/10 hover:text-brand-coral"
                }`}
              >
                {listening && <span className="absolute inset-0 rounded-full bg-status-red/40 animate-ping" />}
                <Microphone size={18} weight={listening ? "fill" : "regular"} className="relative" />
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <span className="text-sm font-semibold text-slate-600">{t("teacherHomework.notifyParentsVia", { defaultValue: "Also notify parents via:" })}</span>
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox data-testid="homework-email-checkbox" checked={channels.send_email} onCheckedChange={(c) => setChannels({ ...channels, send_email: !!c })} />
            {t("teacherHomework.sendEmail", { defaultValue: "Send via Email" })}
          </label>
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox data-testid="homework-whatsapp-checkbox" checked={channels.send_whatsapp} onCheckedChange={(c) => setChannels({ ...channels, send_whatsapp: !!c })} />
            {t("teacherHomework.sendWhatsApp", { defaultValue: "Send via WhatsApp" })}
          </label>
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox data-testid="homework-sms-checkbox" checked={channels.send_sms} onCheckedChange={(c) => setChannels({ ...channels, send_sms: !!c })} />
            {t("teacherHomework.sendSMS", { defaultValue: "Send via SMS" })}
          </label>
        </div>
        {!optIn && (channels.send_email || channels.send_whatsapp || channels.send_sms) && (
          <p className="mt-3 text-xs font-semibold text-amber-700 bg-status-amber/20 rounded-xl px-4 py-2.5" data-testid="homework-opt-in-warning">
            {t("teacherHomework.notificationsDisabled", { defaultValue: "External notifications are disabled for this school. The platform owner must enable the opt-in before Email/WhatsApp alerts work." })}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            type="button"
            data-testid="ai-suggest-homework-btn"
            onClick={suggestWithAI}
            disabled={aiBusy}
            className="rounded-full font-bold bg-gradient-to-r from-brand-yellow to-brand-coral text-white hover:opacity-90 transition-all duration-300 active:scale-95 border-0"
          >
            <Sparkle size={16} weight="duotone" className="mr-1.5" />
            {aiBusy ? t("teacherHomework.writing", { defaultValue: "Writing…" }) : t("teacherHomework.suggestWithAI", { defaultValue: "Suggest with AI" })}
          </Button>
          <Button type="submit" data-testid="homework-post-btn" disabled={posting} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 active:scale-95">
            {posting ? t("teacherHomework.posting", { defaultValue: "Posting…" }) : t("teacherHomework.postToClass", { defaultValue: "Post to class" })}
          </Button>
        </div>
      </form>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`homework-card-${p.id}`}>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${kindBadge[p.kind]}`}>{t(`teacherHomework.${p.kind}`, { defaultValue: p.kind })}</span>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                {homeworkLanguage(p.content_language).label}
              </span>
              <span className="text-xs font-semibold text-slate-500">{p.subject} · {p.class_name}</span>
              <span className="text-xs text-slate-400 ml-auto">{fmtDate(p.created_at)}</span>
            </div>
            <p className="font-heading font-bold mt-2.5">{p.title}</p>
            <p
              lang={p.content_language || "en"}
              className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap"
            >
              {p.content}
            </p>
            <p className="text-xs text-slate-400 mt-2">— {p.teacher_name}</p>
          </div>
        ))}
        {posts.length === 0 && <p className="text-sm text-slate-400 text-center py-10">{t("teacherHomework.nothingPosted", { defaultValue: "Nothing posted yet. Write the first note above!" })}</p>}
      </div>
    </div>
  );
}

function TeacherLeaveTab() {
  const { t } = useTranslation();
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ from_date: todayLocal(), to_date: todayLocal(), reason: "", leave_type: "casual" });
  const [saving, setSaving] = useState(false);

  const [studentLeaves, setStudentLeaves] = useState([]);
  const [myClasses, setMyClasses] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [sForm, setSForm] = useState({ class_id: "", student_id: "", from_date: todayLocal(), to_date: todayLocal(), reason: "" });
  const [savingS, setSavingS] = useState(false);

  const load = () => {
    api.get("/teacher/leaves").then((r) => setLeaves(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/teacher/student-leaves").then((r) => setStudentLeaves(r.data)).catch(() => {});
    api.get("/teacher/classes").then((r) => setMyClasses(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sForm.class_id) {
      setClassStudents([]);
      return;
    }
    api.get("/teacher/students", { params: { class_id: sForm.class_id } }).then((r) => setClassStudents(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [sForm.class_id]);

  const submitStudentLeave = async (e) => {
    e.preventDefault();
    setSavingS(true);
    try {
      await api.post("/teacher/student-leaves", sForm);
      toast.success(t("teacherLeave.studentLeaveSubmitted", { defaultValue: "Student leave submitted for approval" }));
      setSForm({ class_id: "", student_id: "", from_date: todayLocal(), to_date: todayLocal(), reason: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSavingS(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/teacher/leaves", form);
      toast.success(t("teacherLeave.leaveSubmitted", { defaultValue: "Leave application submitted" }));
      setForm({ from_date: todayLocal(), to_date: todayLocal(), reason: "", leave_type: "casual" });
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const badge = (status) => {
    const map = { approved: "bg-status-green/10 text-status-green", rejected: "bg-status-red/10 text-status-red", pending: "bg-status-amber/20 text-amber-600" };
    return (
      <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${map[status]}`}>
        {t(`teacherLeave.status.${status}`, { defaultValue: status })}
      </span>
    );
  };

  return (
    <div data-testid="teacher-leave-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-6">{t("teacherLeave.heading", { defaultValue: "My leave applications" })}</h1>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="teacher-leave-form">
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label htmlFor="leave-from">{t("teacherLeave.from", { defaultValue: "From" })}</Label><Input id="leave-from" data-testid="leave-from-input" type="date" required className="mt-1.5" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} /></div>
          <div><Label htmlFor="leave-to">{t("teacherLeave.to", { defaultValue: "To" })}</Label><Input id="leave-to" data-testid="leave-to-input" type="date" required className="mt-1.5" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} /></div>
          <div>
            <Label>{t("teacherLeave.type", { defaultValue: "Type" })}</Label>
            <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
              <SelectTrigger data-testid="leave-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="casual">{t("teacherLeave.types.casual", { defaultValue: "Casual" })}</SelectItem>
                <SelectItem value="sick">{t("teacherLeave.types.sick", { defaultValue: "Sick" })}</SelectItem>
                <SelectItem value="personal">{t("teacherLeave.types.personal", { defaultValue: "Personal" })}</SelectItem>
                <SelectItem value="other">{t("teacherLeave.types.other", { defaultValue: "Other" })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4"><Label htmlFor="leave-reason">{t("teacherLeave.reason", { defaultValue: "Reason" })}</Label><Textarea id="leave-reason" data-testid="leave-reason-input" required rows={2} className="mt-1.5" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("teacherLeave.reasonPlaceholder", { defaultValue: "Reason for leave…" })} /></div>
        <Button type="submit" data-testid="leave-apply-btn" disabled={saving} className="mt-4 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 active:scale-95">
          {saving ? t("teacherLeave.submitting", { defaultValue: "Submitting…" }) : t("teacherLeave.applyForLeave", { defaultValue: "Apply for leave" })}
        </Button>
      </form>

      <form onSubmit={submitStudentLeave} className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="teacher-student-leave-form">
        <p className="font-heading font-bold mb-4">{t("teacherLeave.applyStudentLeave", { defaultValue: "Apply leave for a student" })}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Select value={sForm.class_id} onValueChange={(v) => setSForm({ ...sForm, class_id: v, student_id: "" })}>
            <SelectTrigger data-testid="sleave-class-select"><SelectValue placeholder={t("teacherLeave.class", { defaultValue: "Class" })} /></SelectTrigger>
            <SelectContent>
              {myClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sForm.student_id} onValueChange={(v) => setSForm({ ...sForm, student_id: v })} disabled={!sForm.class_id}>
            <SelectTrigger data-testid="sleave-student-select"><SelectValue placeholder={t("teacherLeave.student", { defaultValue: "Student" })} /></SelectTrigger>
            <SelectContent>
              {classStudents.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" data-testid="sleave-from" value={sForm.from_date} onChange={(e) => setSForm({ ...sForm, from_date: e.target.value })} required />
          <Input type="date" data-testid="sleave-to" value={sForm.to_date} onChange={(e) => setSForm({ ...sForm, to_date: e.target.value })} required />
          <Input data-testid="sleave-reason" placeholder={t("teacherLeave.reason", { defaultValue: "Reason" })} value={sForm.reason} onChange={(e) => setSForm({ ...sForm, reason: e.target.value })} required />
        </div>
        <Button type="submit" data-testid="sleave-submit-btn" disabled={savingS || !sForm.student_id} className="mt-4 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 active:scale-95">
          {savingS ? t("teacherLeave.submitting", { defaultValue: "Submitting…" }) : t("teacherLeave.submitStudentLeave", { defaultValue: "Submit student leave" })}
        </Button>
      </form>

      {studentLeaves.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider mb-2.5">{t("teacherLeave.studentLeavesMyClasses", { defaultValue: "Student leaves (my classes)" })}</h2>
          <div className="space-y-2">
            {studentLeaves.map((l) => (
              <div key={l.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between" data-testid={`class-student-leave-${l.id}`}>
                <div>
                  <p className="text-sm font-semibold">{l.student_name} <span className="text-xs font-normal text-slate-400">{l.class_name}</span></p>
                  <p className="text-xs text-slate-500">{fmtDate(l.from_date)} → {fmtDate(l.to_date)} · {l.reason}</p>
                </div>
                <div className="flex items-center gap-2">
                  {badge(l.status)}
                  {l.status === "pending" && <>
                    <Button size="sm" onClick={async () => { try { await api.patch(`/teacher/student-leaves/${l.id}`, { status: "approved" }); setStudentLeaves((rows) => rows.map((x) => x.id === l.id ? { ...x, status: "approved" } : x)); toast.success("Leave approved"); } catch (e) { toast.error(formatErr(e)); } }}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={async () => { try { await api.patch(`/teacher/student-leaves/${l.id}`, { status: "rejected" }); setStudentLeaves((rows) => rows.map((x) => x.id === l.id ? { ...x, status: "rejected" } : x)); toast.success("Leave rejected"); } catch (e) { toast.error(formatErr(e)); } }}>Reject</Button>
                  </>}
                  {l.reviewed_by && <span className="text-xs text-slate-400">Reviewed by {l.reviewed_by}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-3">
        {leaves.map((l) => (
          <div key={l.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`my-leave-${l.id}`}>
            <div>
              <p className="font-heading font-bold">{fmtDate(l.from_date)} → {fmtDate(l.to_date)} <span className="text-xs font-semibold text-slate-400 capitalize ml-2">{t(`teacherLeave.types.${l.leave_type}`, { defaultValue: l.leave_type })}</span></p>
              <p className="text-sm text-slate-600 mt-1">{l.reason}</p>
              {l.reviewed_by && <p className="text-xs text-slate-400 mt-1">{t("teacherLeave.reviewedBy", { defaultValue: "reviewed by" })} {l.reviewed_by}</p>}
            </div>
            {badge(l.status)}
          </div>
        ))}
        {leaves.length === 0 && <p className="text-sm text-slate-400 text-center py-8">{t("teacherLeave.noApplications", { defaultValue: "No leave applications yet." })}</p>}
      </div>
    </div>
  );
}

function TeacherReportCardsTab() {
  const [students, setStudents] = useState(null);
  const [reportFor, setReportFor] = useState(null);

  useEffect(() => {
    api.get("/teacher/students").then((r) => setStudents(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);

  return (
    <div data-testid="teacher-report-cards-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-1">Report Cards</h1>
      <p className="text-sm text-slate-500 mb-6">Open a student's report card to review term grades, write remarks and download the PDF. Publishing is done by the school admin.</p>
      {students === null ? (
        <div className="py-14 grid place-items-center"><div className="w-9 h-9 rounded-full border-4 border-brand-coral border-t-transparent animate-spin" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {students.map((s) => (
            <button key={s.id} data-testid={`teacher-rc-${s.id}`} onClick={() => setReportFor(s.id)} className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150 text-left">
              <div>
                <p className="font-semibold">{s.name}</p>
                <p className="text-xs text-slate-400">{s.class_name}{s.roll_no ? ` · Roll ${s.roll_no}` : ""}</p>
              </div>
              <span className="inline-flex items-center gap-2 text-xs font-bold text-brand-coralDark">View report card <FileText size={15} /></span>
            </button>
          ))}
          {students.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No students in your classes yet.</p>}
        </div>
      )}
      {reportFor && <ReportCard studentId={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}

function TeacherExamsTab() {
  const { t } = useTranslation();
  const [exams, setExams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [reportFor, setReportFor] = useState(null);

  const load = () => {
    api.get("/teacher/exams").then((r) => setExams(r.data)).catch((e) => toast.error(formatErr(e)));
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openExam = async (id) => {
    try {
      const { data } = await api.get(`/teacher/exams/${id}/marks`);
      setSelected(data);
      const m = {};
      data.students.forEach((s) => {
        if (s.marks != null) m[s.id] = String(s.marks);
      });
      setMarks(m);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const saveMarks = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(marks)
        .filter(([, v]) => v !== "" && v != null)
        .map(([student_id, v]) => ({ student_id, marks: Number(v) }));
      const { data } = await api.post(`/teacher/exams/${selected.exam.id}/marks`, { marks: payload });
      toast.success(
        t("teacherExams.marksSaved", {
          count: data.saved,
          defaultValue: `Marks saved for ${data.saved} students`,
        })
      );
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (selected) {
    return (
      <div data-testid="exam-marks-view">
        <button onClick={() => setSelected(null)} className="text-sm font-bold text-brand-coralDark mb-4 hover:underline" data-testid="exam-back-btn">
          ← {t("teacherExams.backToExams", { defaultValue: "Back to exams" })}
        </button>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-extrabold">{selected.exam.name}</h1>
            <p className="text-sm text-slate-500">
              {selected.exam.subject} · {fmtDate(selected.exam_date)} · {t("teacherExams.outOf", { defaultValue: "out of" })} {selected.exam.max_marks} {t("teacherExams.marks", { defaultValue: "marks" })}
            </p>
          </div>
          <Button data-testid="save-marks-btn" onClick={saveMarks} disabled={saving} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-6 transition-transform duration-300 active:scale-95">
            {saving ? t("teacherExams.saving", { defaultValue: "Saving…" }) : t("teacherExams.saveMarks", { defaultValue: "Save marks" })}
          </Button>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3">{t("teacherExams.student", { defaultValue: "Student" })}</th>
                <th className="px-5 py-3">{t("teacherExams.roll", { defaultValue: "Roll" })}</th>
                <th className="px-5 py-3 w-40">{t("teacherExams.marks", { defaultValue: "Marks" })} (/{selected.exam.max_marks})</th>
              </tr>
            </thead>
            <tbody>
              {selected.students.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`marks-row-${s.id}`}>
                  <td className="px-5 py-3 font-semibold">
                    <span className="inline-flex items-center gap-2">{s.name}
                      <button data-testid={`marks-report-${s.id}`} onClick={() => setReportFor(s.id)} className="p-1 rounded-md text-slate-300 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200" title={t("teacherExams.viewReportCard", { defaultValue: "View report card" })}><FileText size={14} /></button>
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{s.roll_no || "—"}</td>
                  <td className="px-5 py-3">
                    <Input
                      type="number"
                      min="0"
                      max={selected.exam.max_marks}
                      data-testid={`marks-input-${s.id}`}
                      value={marks[s.id] ?? ""}
                      onChange={(e) => setMarks({ ...marks, [s.id]: e.target.value })}
                      className="w-28"
                      placeholder="—"
                    />
                  </td>
                </tr>
              ))}
              {selected.students.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-5 py-10 text-center text-sm text-slate-400">
                    {t("teacherExams.noStudents", { defaultValue: "No students in this class." })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {reportFor && <ReportCard studentId={reportFor} onClose={() => setReportFor(null)} />}
      </div>
    );
  }

  return (
    <div data-testid="teacher-exams-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-2">
        {t("teacherExams.heading", { defaultValue: "Exams" })}
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        {t("teacherExams.description", { defaultValue: "Select an exam to enter or update marks for your class." })}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {exams.map((e) => (
          <button
            key={e.id}
            data-testid={`exam-open-${e.id}`}
            onClick={() => openExam(e.id)}
            className="bg-white border border-slate-200 rounded-2xl p-5 text-left hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:border-brand-coral/40 transition-all duration-300"
          >
            <p className="font-heading font-bold">{e.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{e.subject} · {e.class_name}</p>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-600">{fmtDate(e.exam_date)} · /{e.max_marks}</span>
              <span className="font-bold text-brand-coralDark">
                {e.marks_entered} {t("teacherExams.graded", { defaultValue: "graded" })}
              </span>
            </div>
          </button>
        ))}
        {exams.length === 0 && (
          <p className="text-sm text-slate-400 py-6">
            {t("teacherExams.noExams", { defaultValue: "No exams yet — the school admin creates them." })}
          </p>
        )}
      </div>
    </div>
  );
}


function TeacherFeeConfirmations() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get('/teacher/fees/pending-confirmation')
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  const confirm = async (id) => {
    try {
      await api.post(`/teacher/fees/payments/${id}/confirm`);
      toast.success('Payment confirmed');
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return <div data-testid="teacher-fee-confirmations"><h1 className="font-heading text-2xl font-extrabold mb-1">Fee Confirmations</h1><p className="text-sm text-slate-500 mb-5">Parent-reported payments for students in your class. Confirm only after checking the school payment record.</p><div className="space-y-3">{rows.map((r) => <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"><div><p className="font-heading font-bold">{r.student_name}</p><p className="text-xs text-slate-500 mt-1">{r.student_reference_id || 'Student reference pending'} · {fmtDate(r.payment_date)}</p><p className="text-sm mt-2">₹{Number(r.amount || 0).toLocaleString('en-IN')} · {(r.method || '').replaceAll('_',' ').toUpperCase()} {r.reference ? `· ${r.reference}` : ''}</p></div><Button onClick={() => confirm(r.id)} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">Confirm received</Button></div>)}{rows.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-400">No parent-reported payments are waiting for your confirmation.</div>}</div></div>;
}
